import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, FileText, Loader2, Eye } from 'lucide-react';

const RESET_OPTIONS = [
  { value: 'none', label: 'No Reset' },
  { value: 'yearly', label: 'Reset Yearly' },
  { value: 'monthly', label: 'Reset Monthly' },
];

export default function NumberSeries() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/settings/number-series').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (docType, field, value) => {
    setData((d) => ({
      ...d,
      [docType]: { ...d[docType], [field]: ['padding', 'current'].includes(field) ? Number(value) : value },
    }));
  };

  const preview = (entry) => {
    const num = String(entry.current).padStart(entry.padding, '0');
    return `${entry.prefix || ''}${num}${entry.suffix || ''}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/number-series', { series: data });
      toast.success('Number series saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="number-series-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Number Series" title="Number Series" subtitle="Auto-numbering rules for PO, GRN, Invoice, WO, NCR, RA Bill, MPR, RFQ." icon={FileText} />

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mt-6">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
            <tr>
              <th className="text-left py-2.5 px-3">Document Type</th>
              <th className="text-left py-2.5 px-3">Prefix</th>
              <th className="text-left py-2.5 px-3">Padding</th>
              <th className="text-left py-2.5 px-3">Current #</th>
              <th className="text-left py-2.5 px-3">Suffix</th>
              <th className="text-left py-2.5 px-3">Reset Cycle</th>
              <th className="text-left py-2.5 px-3">Preview</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([docType, entry]) => (
              <tr key={docType} className="border-t border-slate-100" data-testid={`ns-row-${docType}`}>
                <td className="py-2 px-3 font-semibold">{docType}</td>
                <td className="py-2 px-3">
                  <input type="text" value={entry.prefix} onChange={(e) => update(docType, 'prefix', e.target.value)} className="w-28 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" />
                </td>
                <td className="py-2 px-3">
                  <input type="number" min="1" max="10" value={entry.padding} onChange={(e) => update(docType, 'padding', e.target.value)} className="w-16 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" />
                </td>
                <td className="py-2 px-3">
                  <input type="number" min="1" value={entry.current} onChange={(e) => update(docType, 'current', e.target.value)} className="w-20 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" />
                </td>
                <td className="py-2 px-3">
                  <input type="text" value={entry.suffix || ''} onChange={(e) => update(docType, 'suffix', e.target.value)} className="w-24 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" />
                </td>
                <td className="py-2 px-3">
                  <select value={entry.reset} onChange={(e) => update(docType, 'reset', e.target.value)} className="w-32 border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white">
                    {RESET_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </td>
                <td className="py-2 px-3 text-xs">
                  <span className="font-mono inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-[#1F7A6E] rounded-sm" data-testid={`preview-${docType}`}>
                    <Eye size={11} /> {preview(entry)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end sticky bottom-4">
        <button data-testid="ns-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50 shadow-lg">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Series'}
        </button>
      </div>
    </div>
  );
}
